import React, { useContext, useEffect } from 'react';

import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import { useTheme } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import { CurrencyField } from '../../../common/CurrencyInput';
import { LancamentoContaPagarContext } from '../LancamentoContaPagarContext';
import { ModalSizeEditableContextProvider, ModalSizeEditableContext } from '../../../common/ModalSizeEditableContext';
import { TooltipIconButton } from '../../../common/TooltipIconButton';
import TooltipBar from '../../../common/TooltipBar';

import { ModalIncluirNovaDespesa, ModalEditarDespesa, ModalExcluirDespesa } from './modaisDespesas';

const TooltipButtonIncluirDespesa = () => {
	const { setModalValue, setMinWidthModel } = useContext(ModalSizeEditableContext);
	return (
		<TooltipIconButton
			size='small'
			title='Incluir Despesa'
			onClick={() => {
				setMinWidthModel('80vw');
				setModalValue((v) => ({
					...v,
					open: true,
					dialogBody: <ModalIncluirNovaDespesa context={ModalSizeEditableContext} />,
				}));
			}}
		>
			<AddCircleOutlineIcon />
		</TooltipIconButton>
	);
};

const TooltipButtonEditaDespesa = (record) => {
	const { setModalValue, setMinWidthModel } = useContext(ModalSizeEditableContext);
	return (
		<TooltipIconButton
			size='small'
			title='Editar Despesa'
			onClick={() => {
				setMinWidthModel('80vw');
				setModalValue((v) => ({
					...v,
					open: true,
					dialogBody: <ModalEditarDespesa context={ModalSizeEditableContext} record={record} />,
				}));
			}}
		>
			<EditIcon />
		</TooltipIconButton>
	);
};

const TooltipButtonExcluirDespesa = (record) => {
	const { setModalValue, setMinWidthModel } = useContext(ModalSizeEditableContext);
	return (
		<TooltipIconButton
			size='small'
			title='Excluir Despesa'
			onClick={() => {
				setMinWidthModel('55vw');
				setModalValue((v) => ({
					...v,
					open: true,
					dialogBody: <ModalExcluirDespesa record={record} />,
				}));
			}}
		>
			<DeleteOutlineIcon />
		</TooltipIconButton>
	);
};

const tipoLancamentoChips = {
	C: <Chip label='Condomínio' color='primary' size='small' />,
	G: <Chip label='Grupo' color='secondary' size='small' />,
};

const TableCellLeft = (props) => <TableCell align='left' {...props} />;

const TableCellCenter = (props) => <TableCell align='center' {...props} />;

const TableCellRight = (props) => <TableCell align='right' {...props} />;

const InfoDespesa = ({
	id,
	codigo,
	conta_despesa,
	tipo_lancamento,
	condominio,
	grupo,
	quantidade,
	valor_unitario,
	valor_total,
	anexo_nf,
	anexo_ordem_compra_servico,
}) => {
	const { parcelas } = useContext(LancamentoContaPagarContext);
	return (
		<TableRow>
			<TableCellCenter
				style={{
					paddingLeft: 0,
					paddingRight: 0,
					width: '5%',
					whiteSpace: 'nowrap',
				}}
			>
				{codigo}
			</TableCellCenter>
			<TableCellLeft
				style={{
					paddingLeft: 0,
					paddingRight: 0,
					width: '20%',
					whiteSpace: 'nowrap',
				}}
			>
				{conta_despesa?.nome}
			</TableCellLeft>
			<TableCellCenter
				align='center'
				style={{ paddingLeft: 0, paddingRight: 0, width: '1%', whiteSpace: 'nowrap' }}
			>
				{tipo_lancamento && tipoLancamentoChips[tipo_lancamento.id]}
			</TableCellCenter>
			<TableCellCenter style={{ paddingLeft: 0, paddingRight: 0 }}>{grupo?.nome}</TableCellCenter>
			<TableCellCenter style={{ paddingLeft: 0, paddingRight: 0 }}>{quantidade || '0'}</TableCellCenter>
			<TableCellRight style={{ paddingLeft: 0, paddingRight: 0 }}>
				<CurrencyField
					prefix='R$ '
					minimumFractionDigits={2}
					value={valor_unitario || 0}
					id={`valor_unitario-${codigo}`}
				/>
			</TableCellRight>
			<TableCellRight style={{ paddingLeft: 0, paddingRight: 0 }}>
				<CurrencyField
					prefix='R$ '
					minimumFractionDigits={2}
					value={valor_total || 0}
					id={`valor_total-${codigo}`}
				/>
			</TableCellRight>
			<TableCellCenter style={{ paddingLeft: 0, paddingRight: 0 }}>
				{parcelas.length < 2 && (parcelas[0] ? parcelas[0].situacao === 'P' : true) && (
					<TooltipBar>
						<TooltipButtonEditaDespesa
							{...{
								id,
								codigo,
								tipo_lancamento,
								condominio,
								grupo,
								quantidade,
								valor_unitario,
								valor_total,
								conta_despesa,
								anexo_nf,
								anexo_ordem_compra_servico,
							}}
							key='edita-button'
						/>
						<TooltipButtonExcluirDespesa
							{...{
								id,
								codigo,
							}}
							key='exclui-button'
						/>
					</TooltipBar>
				)}
			</TableCellCenter>
		</TableRow>
	);
};

const TabelaDespesas = () => {
	const { despesas, selectedCondominio, parcelas, erros, setErros } = useContext(LancamentoContaPagarContext);
	const {
		palette: { syndikosRed },
	} = useTheme();
	const validate = () => {
		if (!despesas || !despesas.length) {
			setErros((e) => ({
				...e,
				despesas: 'Inclua ao menos uma despesa',
			}));
		} else if (erros?.despesas) {
			setErros((e) => {
				const { despesas, ...rest } = e;
				return rest;
			});
		}
	};
	useEffect(validate, [despesas]);

	return (
		<Box>
			{erros?.despesas && (
				<Typography style={{ fontWeight: 'bold', color: syndikosRed.main }}>{erros.despesas}</Typography>
			)}
			{selectedCondominio && parcelas.length < 2 && (parcelas[0] ? parcelas[0].situacao === 'P' : true) && (
				<TooltipButtonIncluirDespesa />
			)}
			<Table size='small' padding='default'>
				<TableHead>
					<TableRow>
						<TableCellCenter
							style={{
								paddingLeft: 0,
								paddingRight: 0,
								width: '5%',
								whiteSpace: 'nowrap',
							}}
						>
							#
						</TableCellCenter>
						<TableCellLeft
							style={{
								paddingLeft: 0,
								paddingRight: 0,
								width: '20%',
								whiteSpace: 'nowrap',
							}}
						>
							Despesa
						</TableCellLeft>
						<TableCellCenter
							style={{
								paddingLeft: 0,
								paddingRight: 0,
								width: '10%',
								whiteSpace: 'nowrap',
							}}
						>
							Tipo Lançamento
						</TableCellCenter>
						<TableCellCenter
							style={{
								paddingLeft: 0,
								paddingRight: 0,
								width: '20%',
								whiteSpace: 'nowrap',
							}}
						>
							Grupo
						</TableCellCenter>
						<TableCellCenter
							style={{
								paddingLeft: 0,
								paddingRight: 0,
								width: '5%',
								whiteSpace: 'nowrap',
							}}
						>
							Quantidade
						</TableCellCenter>
						<TableCellRight
							style={{
								paddingLeft: 0,
								paddingRight: 0,
								width: '15%',
								whiteSpace: 'nowrap',
							}}
						>
							Valor Unitário
						</TableCellRight>
						<TableCellRight
							style={{
								paddingLeft: 0,
								paddingRight: 0,
								width: '15%',
								whiteSpace: 'nowrap',
							}}
						>
							Valor Total
						</TableCellRight>
						<TableCellCenter
							style={{
								paddingLeft: 0,
								paddingRight: 0,
								width: '10%',
								whiteSpace: 'nowrap',
							}}
						></TableCellCenter>
					</TableRow>
				</TableHead>
				<TableBody>
					{despesas?.length > 0 &&
						despesas?.map((despesa, index) => <InfoDespesa key={`despesa-${index}`} {...despesa} />)}
				</TableBody>
			</Table>
		</Box>
	);
};

const ListDespesas = () => (
	<ModalSizeEditableContextProvider
		customDialogProps={{
			disableBackdropClick: true,
			disableEscapeKeyDown: true,
			minWidth: '80vw',
		}}
	>
		<TabelaDespesas />
	</ModalSizeEditableContextProvider>
);

export default ListDespesas;
